/**
 * Why the chief proposed what it did, as a sentence in the console's language.
 *
 * The server used to compose that sentence and send it, which put English into a
 * Turkish interface on the first screen an operator opens — "The agent finished
 * a turn; propose a review-and-continue…", under a Turkish heading, above a
 * Turkish button. Interface text comes from the translation table (CLAUDE.md),
 * and a sentence assembled on a machine that does not know who is reading cannot.
 *
 * So a deterministic proposal names its reason with a kind and its parameters —
 * the shape `riskSentence` and `adviceSentence` already use — and this composes
 * it. Three things fall back to the server's own words, each on purpose:
 *
 *  - a model's reasoning, which is a sentence the model wrote and has no kind;
 *  - a kind added to the server after this build, which must still say something;
 *  - an error string quoted inside a note, which is the machine's own text.
 *
 * The *prompt* is not translated and must not be: it is the message that will be
 * typed at an agent, not text about the console, and the model path produces it
 * in whatever language the transcript is in.
 */

import type { useT } from '@/i18n/context'

export interface ChiefReasoned {
  reasoning?: string
  reasonKind?: string
  reasonParams?: Record<string, string>
  noteKind?: string
  noteParams?: Record<string, string>
}

/** The reason itself: what the chief concluded about this session. */
export function chiefReasonSentence(p: ChiefReasoned, t: ReturnType<typeof useT>): string {
  const q = p.reasonParams ?? {}
  switch (p.reasonKind) {
    case 'session_ended':
      return t('chief.reason.session_ended')
    case 'agent_working':
      return t('chief.reason.agent_working')
    case 'agent_waiting':
      // The session says why it stopped only sometimes. Without a reason the
      // sentence is a different sentence, not the same one with an empty pair
      // of brackets in the middle of it.
      return q.reason
        ? t('chief.reason.agent_waiting', { reason: q.reason })
        : t('chief.reason.agent_waiting_plain')
    case 'tool_errors':
      return t('chief.reason.tool_errors', { errors: q.errors ?? '' })
    case 'turn_finished':
      return t('chief.reason.turn_finished')
    case 'no_signal':
      return t('chief.reason.no_signal')
    case 'unstructured':
      return t('chief.reason.unstructured')
    default:
      return p.reasoning ?? ''
  }
}

/** The note: why the model could not be asked, so the rules answered instead.
 *  Empty when the analysis ran. */
export function chiefNoteSentence(p: ChiefReasoned, t: ReturnType<typeof useT>): string {
  const q = p.noteParams ?? {}
  switch (p.noteKind) {
    case 'account_unreachable':
      return t('chief.note.account_unreachable')
    case 'no_transcript':
      return t('chief.note.no_transcript')
    case 'analysis_failed':
      return t('chief.note.analysis_failed', { error: q.error ?? '' })
    // ADR-0025. Which subscription answered, and — where a designated one could
    // not — that nothing else was spent in its place. Neither takes a parameter:
    // the specific refusal is the server's own sentence and travels as
    // `noteDetail`, shown beside this one rather than interpolated into it,
    // because a clause of the wrong language inside a translated sentence is the
    // defect the reason kinds exist to end.
    case 'no_manager':
      return t('chief.note.no_manager')
    case 'manager_unusable':
      return t('chief.note.manager_unusable')
    default:
      return ''
  }
}
